import { useEffect, useRef } from 'react';
import { useCameraViewOffset } from '../../context/CameraViewOffsetContext';

const EXPAND_DURATION = 400;

const TRANSIENT_PROPS = [
  '--panel-current-height',
  '--canvas-panel-gap',
  '--chat-sidebar-width',
  '--canvas-side-gap',
  '--panel-transition-duration',
] as const;

/**
 * Briefly extends the scene canvas to the full visible area between the
 * splat preloader finishing and the HUD appearing — gives a half-second
 * "wow" moment where the user sees the unobstructed reveal before the
 * chat / sidebar slides in.
 *
 * Writes every layout-shrink CSS-var on `#app` to zero (both the mobile
 * bottom curtain and the desktop right sidebar), so canvas + overlays
 * expand on whichever layout is active.
 *
 * IMPORTANT: cleanup restores the EXACT inline values that were on
 * `#app` BEFORE mount — including possibly-pre-existing App-level
 * overrides (e.g. `App.tsx`'s `layoutMode === 'desktop'` useLayoutEffect
 * which zeroes the mobile vars during the preview phase). A naive
 * `root.style.cssText = ''` on cleanup would wipe those overrides too,
 * leaving the canvas inheriting `var(--panel-default-height)` = 106 px
 * + `var(--canvas-panel-gap)` = 24 px from spacing.css :root defaults
 * for the brief moment between cleanup and AssistantChatSidebar mount.
 */
export const SceneExpandedEffect = () => {
  const { applyCurtainOffset } = useCameraViewOffset();
  const applyRef = useRef(applyCurtainOffset);
  applyRef.current = applyCurtainOffset;

  useEffect(() => {
    const root = document.getElementById('app');
    if (!root) return;

    const before = new Map<string, string>();
    for (const prop of TRANSIENT_PROPS) {
      before.set(prop, root.style.getPropertyValue(prop));
    }

    root.style.setProperty('--panel-current-height', '0px');
    root.style.setProperty('--canvas-panel-gap', '0px');
    root.style.setProperty('--chat-sidebar-width', '0px');
    root.style.setProperty('--canvas-side-gap', '0px');
    root.style.setProperty('--panel-transition-duration', `${EXPAND_DURATION}ms`);

    // `applyCurtainOffset(0)` writes `{top: safeArea.top, right: 0,
    // bottom: 0, left: 0}` — covers both mobile (bottom: 0 collapses
    // the curtain) and desktop (right: 0 zeroes the sidebar shift)
    // through the unified `applyLayoutOffset` path.
    applyRef.current(0, true, EXPAND_DURATION);

    return () => {
      for (const prop of TRANSIENT_PROPS) {
        const previous = before.get(prop);
        if (previous) {
          root.style.setProperty(prop, previous);
        } else {
          root.style.removeProperty(prop);
        }
      }
    };
  }, []);

  return null;
};
